import { useEffect, useState, useCallback } from "react";
import { ChevronUp, Plus, Lightbulb, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/pages/Dashboard";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const STATUS_STYLE = {
  open: "bg-white/10 text-[#A1A1AA]", planned: "bg-blue-500/15 text-blue-300",
  in_progress: "bg-amber-500/15 text-amber-300", completed: "bg-emerald-500/15 text-emerald-300",
  rejected: "bg-red-500/15 text-red-300",
};

export default function FeatureRequests() {
  const [reqs, setReqs] = useState([]);
  const [form, setForm] = useState({ title: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/feature-requests").then((r) => setReqs(r.data.requests)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/feature-requests", form);
      toast.success("Idea submitted — thank you!");
      setForm({ title: "", description: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const vote = async (id) => {
    try { const { data } = await api.post(`/feature-requests/${id}/vote`); 
      setReqs((rs) => rs.map((r) => r.id === id ? { ...r, votes: data.votes, voted: data.voted } : r));
    } catch { toast.error("Could not vote"); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="feature-requests-page">
      <PageHeader eyebrow="Your voice shapes the product" title="Feature Requests"
        subtitle="Share ideas, vote on what matters, and watch them land on our roadmap." />

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-5">
        <div className="flex items-center gap-2 mb-3 text-white font-medium"><Plus className="w-4 h-4 text-[#D4AF37]" /> Suggest an idea</div>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="A short, clear title" data-testid="fr-title" className="bg-[#0d0d0f] border-white/10 text-white mb-2" />
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Describe your idea (optional)" data-testid="fr-description" className="bg-[#0d0d0f] border-white/10 text-white mb-3" />
        <Button onClick={submit} disabled={busy || form.title.trim().length < 4} data-testid="fr-submit"
          className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">Submit idea</Button>
      </div>

      {loading ? <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#D4AF37]" /></div> :
       reqs.length === 0 ? (
        <div className="text-center py-10 text-[#52525B]"><Lightbulb className="w-8 h-8 mx-auto mb-2" />No ideas yet — be the first!</div>
       ) : reqs.map((r) => (
        <div key={r.id} className="rounded-xl border border-white/8 bg-[#121214] p-4 flex gap-4" data-testid={`fr-${r.id}`}>
          <button onClick={() => vote(r.id)} data-testid={`fr-vote-${r.id}`}
            className={`flex flex-col items-center justify-center rounded-lg px-3 py-1.5 border shrink-0 ${r.voted ? "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 text-[#A1A1AA] hover:text-white"}`}>
            <ChevronUp className="w-4 h-4" /><span className="text-sm font-semibold">{r.votes}</span>
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium text-sm">{r.title}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[r.status] || STATUS_STYLE.open}`}>{r.status.replace("_", " ")}</span>
            </div>
            {r.description && <p className="text-xs text-[#71717A] mt-1">{r.description}</p>}
            <p className="text-[10px] text-[#52525B] mt-1">by {r.author} · {r.comments?.length || 0} comments</p>
          </div>
        </div>
      ))}
    </div>
  );
}
